/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2023 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getPageLinkByNodeId } from "@insite/client-framework/Store/Links/LinksSelectors";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Accordion, { AccordionPresentationProps } from "@insite/mobius/Accordion";
import AccordionSection, { AccordionSectionPresentationProps } from "@insite/mobius/AccordionSection";
import Hidden from "@insite/mobius/Hidden";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as cssLinter from "css";
import * as React from "react";
import styled, { css } from "styled-components";

const enum fields {
    accountLinksSections = "accountLinksSections",
    customCSS = "customCSS",
}

const enum accountLinksSectionFields {
    sectionLabel = "sectionLabel",
    links = "links",
}

const enum linkFields {
    url = "url",
    linkText = "linkText",
    openInNewTab = "openInNewTab",
}

type LinkModel = {
    fields: {
        url: LinkFieldValue;
        linkText: string;
        openInNewTab: boolean;
    };
};

type SectionModel = {
    fields: {
        sectionLabel: string;
        links: LinkModel[];
    };
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.accountLinksSections]: SectionModel[];
        [fields.customCSS]: string;
    };
}

interface ListStyleProp {}

const ListStyle = styled.ul<ListStyleProp>`
    li {
        list-style: none;
    }
`;

export interface HeaderSignInStyles {
    sectionLabel?: TypographyPresentationProps;
    accordionStyle?: AccordionPresentationProps;
    accordionSectionStyle?: AccordionSectionPresentationProps;
    accordionLinkStyle?: LinkPresentationProps;
    container?: InjectableCss;
}

export const headerSignInStyles: HeaderSignInStyles = {
    container: {
        css: css`
            .navigation-list-section {
                margin-bottom: 25px;
            
            .title {
                font-size: 20px;
                font-weight: 500;
                line-height: 32px;
                margin-bottom: 12px;
                display: block;
                color: rgba(51,51,51,1);
                text-transform: capitalize;
            }
        }
        `,
    },
    sectionLabel: {
        variant: "h4",
        css: css`
            text-transform: capitalize;
        `,
    },
    accordionStyle: {
        css: css`
            margin-top: 10px;
        `,
    },
    accordionSectionStyle: {
        panelProps: {
            css: css`
                padding-top: 0;
            `,
        },
        titleTypographyProps: {
            weight: "bold",
            size: 18,
            css: css`
                text-transform: capitalize;
            `,
        },
    },
    accordionLinkStyle: {
        css: css`
            display: block;
        `,
    },
};
const styles = headerSignInStyles;

export const NavigationList: React.FC<OwnProps> = ({ fields }) => {
    const customCssWrapper = {
        css: css`
            .navigation-list {
                .navigation-list-item {
                    list-style: none;
                }
            }
            ${fields?.customCSS}
        `,
    };

    const sections = fields?.accountLinksSections;

    return (
        <StyledWrapper {...customCssWrapper} {...styles.container}>
            <Hidden below="lg">
                <ListStyle className="navigation-list" data-test-selector="navigationList">
                    {sections.map((section: SectionModel, index) => {
                        const sectionLabel = section.fields.sectionLabel;
                        const SectionLinks = section.fields.links;
                        return (
                            <li key={sectionLabel + index}>
                                <ul className="navigation-list-section">
                                    <li className="title">{sectionLabel}</li>
                                    {SectionLinks.map((link: LinkModel, index) => {
                                        const linkItem = useGetLink(link.fields.url);
                                        const linkURL = linkItem.url;

                                        if ((linkItem as any).excludeFromNavigation) {
                                            return null;
                                        }

                                        return (
                                            <li
                                                key={index}
                                                className="navigation-list-link"
                                                data-test-selector={`navigationList-${link.fields.linkText}`}
                                            >
                                                <Link
                                                    href={linkURL}
                                                    target={link.fields.openInNewTab ? "_blank" : ""}
                                                    color="secondary"
                                                >
                                                    {link.fields.linkText}
                                                </Link>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </li>
                        );
                    })}
                </ListStyle>
            </Hidden>
            <Hidden above="md">
                <StyledWrapper>
                    <Accordion headingLevel={2} {...styles.accordionStyle}>
                        {sections.map((section: SectionModel, index) => {
                            const sectionLabel = section.fields.sectionLabel;
                            const SectionLinks = section.fields.links;
                            return (
                                <AccordionSection
                                    key={sectionLabel + index}
                                    title={sectionLabel}
                                    {...styles.accordionSectionStyle}
                                >
                                    {SectionLinks.map((link: LinkModel, index) => {
                                        const linkItem = useGetLink(link.fields.url);
                                        const linkURL = linkItem.url;

                                        if ((linkItem as any).excludeFromNavigation) {
                                            return null;
                                        }

                                        return (
                                            <Link
                                                key={index}
                                                className="navigation-list-link"
                                                data-test-selector={`navigationList-${link.fields.linkText}`}
                                                href={linkURL}
                                                target={link.fields.openInNewTab ? "_blank" : ""}
                                                color="secondary"
                                                {...styles.accordionLinkStyle}
                                            >
                                                {link.fields.linkText}
                                            </Link>
                                        );
                                    })}
                                </AccordionSection>
                            );
                        })}
                    </Accordion>
                </StyledWrapper>
            </Hidden>
        </StyledWrapper>
    );
};

const defaultCustomCss = `.navigation-list{
}

.navigation-list-item {
}

.navigation-list-link {
}
.signout-clickable {
}
`;

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: NavigationList,
    definition: {
        group: "Basic",
        icon: "link-simple",
        fieldDefinitions: [
            {
                name: fields.accountLinksSections,
                editorTemplate: "ListField",
                fieldType: "Translatable",
                defaultValue: [],
                tab: basicTab,
                getDisplay: ({ fields: { sectionLabel } }) => sectionLabel,
                fieldDefinitions: [
                    {
                        name: accountLinksSectionFields.sectionLabel,
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: accountLinksSectionFields.links,
                        editorTemplate: "ListField",
                        defaultValue: [],
                        getDisplay: (item: HasFields, state) => {
                            const {
                                url: { type, value },
                                linkText,
                            } = item.fields;
                            if (linkText) {
                                return linkText;
                            }
                            if (type === "Page") {
                                const link = getPageLinkByNodeId(state, value);
                                return link?.title;
                            }
                            if (type === "Category") {
                                let displayValue = "";
                                const categories = (state as any).pageEditor.categories;
                                if (!categories) {
                                    displayValue = "Loading...";
                                } else {
                                    const matchingCategories = categories.filter((o: { id: string }) => o.id === value);
                                    if (matchingCategories.length === 0) {
                                        displayValue = "Unknown";
                                    } else {
                                        displayValue = matchingCategories[0].shortDescription;
                                    }
                                }

                                return displayValue;
                            }
                            if (type === "Url") {
                                return value;
                            }
                            return value;
                        },
                        fieldDefinitions: [
                            {
                                name: linkFields.url,
                                editorTemplate: "LinkField",
                                isRequired: true,
                                defaultValue: {
                                    type: "Page",
                                    value: "",
                                },
                            },
                            {
                                name: linkFields.linkText,
                                editorTemplate: "TextField",
                                defaultValue: "",
                                isRequired: false,
                                validate: (value, item) => (value.length > 40 ? "max allowed characters is 40" : null),
                            },
                            {
                                name: linkFields.openInNewTab,
                                editorTemplate: "CheckboxField",
                                defaultValue: false,
                            },
                        ],
                    },
                ],
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};
export default widgetModule;
